import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Service,
  FetchPriceDetailsDocument,
  FetchPriceDetailsQuery,
  FetchPriceDetailsQueryVariables,
} from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { useWatchTenderById } from "@/hooks/cache";
import { useQuery } from "@apollo/client/react";
import { convertTenderToPriceObject } from "@/utils/helpers";
import TenderCompactPricingCard from "./Sections/TenderCompactPricingCard";
import ServiceCompactPricingCard from "./Sections/ServiceCompactPricingCard";

/**
 * Component to render calculation information in a compact, space-efficient layout.
 * Displays tender and service pricing details with copy functionality for each field.
 *
 * @component
 * @example
 * <CalculationsInfoComponent />
 */
function CalculationsInfoComponent() {
  const { t } = useTranslation(["components", "common", "translation"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const navigate = useNavigate();
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { data: servicesData, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(Number(tenderID), true);
  const watchTenderData = useWatchTenderById(Number(tenderID));
  const tenderData = watchTenderData.data;

  // Fetch tender price details from backend
  const { data: tenderPriceData, loading: tenderPriceLoading } = useQuery<
    FetchPriceDetailsQuery,
    FetchPriceDetailsQueryVariables
  >(FetchPriceDetailsDocument, {
    variables: {
      tenderId: Number(tenderID),
      includeTenderPriceDetails: true,
      includePricingPerService: false,
      includeStandaloneCars: false,
      includeStandalonePurchases: false,
    },
    fetchPolicy: "cache-and-network",
  });

  if (servicesLoading || tenderPriceLoading || !tenderData || !tenderByIdData) {
    return (
      <div className="h-full p-4 space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const services = (servicesData?.fetchServicesOnTender?.services ?? []) as Service[];
  const tenderPriceDetails = tenderPriceData?.fetchPriceDetails?.tenderPriceDetails;

  // Convert tender price details to PriceObject
  const tenderPriceObject = tenderPriceDetails ? convertTenderToPriceObject(tenderPriceDetails) : null;

  // If there are no services on the tender, render empty state
  if (services.length === 0) {
    return (
      <div className="h-full flex flex-col min-h-0">
        <ScrollArea className="flex-1 min-h-0">
          <div className="h-full p-4 flex items-center justify-center">
            <Empty className="max-w-xl mx-auto">
              <EmptyHeader>
                <EmptyTitle>{t("pricedetails.empty.title")}</EmptyTitle>
                <EmptyDescription>{t("pricedetails.empty.description")}</EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button onClick={() => navigate(`/tender/${tenderID}/services/all`)}>
                  {t("pricedetails.empty.addservicebutton")}
                </Button>
              </EmptyContent>
            </Empty>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Split services into regular and additional
  const regularServices = services.filter((service) => !service.additional);
  const additionalServices = services.filter((service) => service.additional);

  return (
    <div className="h-full flex flex-col min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-4 pb-[300px]">
          {/* Tender Price Details */}
          {tenderPriceObject && (
            <div data-cy="price-details-tender-container" className="space-y-2 rounded-md">
              <div className="text-sm font-semibold">{t("pricedetails.labels.tender")}</div>
              <TenderCompactPricingCard priceObject={tenderPriceObject} />
            </div>
          )}

          {/* Services Price Details */}
          {regularServices.length > 0 && (
            <div data-cy="price-details-services-container" className="space-y-3">
              <div className="text-sm font-semibold">{t("pricedetails.labels.services")}</div>
              <Accordion type="multiple" className="w-full space-y-2">
                {regularServices.map((service) => (
                  <AccordionItem
                    key={`service-price-item-${service.id}`}
                    value={`service-${service.id}`}
                    className="border-none"
                  >
                    <Card className="p-0 rounded-md">
                      <AccordionTrigger
                        className="px-3 py-2 hover:no-underline"
                        headerClassName="sticky top-0 z-10 bg-background shadow-bottom-sm"
                      >
                        <div className="flex items-center justify-between w-full pr-2">
                          <span className="font-semibold text-sm text-left">{service.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {t(`common:servicetype.${service.type?.toLowerCase()}`)}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3 pt-0">
                        <ServiceCompactPricingCard service={service} showName={false} useCard={false} />
                      </AccordionContent>
                    </Card>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Additional Services Price Details */}
          {additionalServices.length > 0 && (
            <div data-cy="price-details-additional-services-container" className="space-y-3">
              <div className="text-sm font-semibold">{t("pricedetails.labels.additional")}</div>
              <Accordion type="multiple" className="w-full space-y-2">
                {additionalServices.map((service) => (
                  <AccordionItem
                    key={`additional-service-price-item-${service.id}`}
                    value={`additional-service-${service.id}`}
                    className="border-none"
                  >
                    <Card className="p-0 rounded-md">
                      <AccordionTrigger
                        className="px-3 py-2 hover:no-underline"
                        headerClassName="sticky top-0 z-10 bg-background shadow-bottom-sm"
                      >
                        <div className="flex items-center justify-between w-full pr-2">
                          <span className="font-semibold text-sm text-left">{service.name}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3 pt-0">
                        <ServiceCompactPricingCard service={service} showName={false} useCard={false} />
                      </AccordionContent>
                    </Card>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default CalculationsInfoComponent;
